/* eslint-disable @typescript-eslint/no-explicit-any */
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

/**
 * FleetONE — Consolidated PDF PRO (A4 nativo, corporativo)
 * - NÃO imprime HTML.
 * - Desenha layout profissional usando jsPDF + autoTable.
 *
 * Exporta:
 * - generateConsolidatedPDF_Pro(input) -> Blob
 * - buildConsolidatedPdfInput(params) -> ConsolidatedPdfInput
 */

export type PdfMode = "executive" | "top-n" | "complete";

export type FleetVehicleCostCategory =
  | "fuel"
  | "maintenance"
  | "tires"
  | "expenses"
  | "documents"
  | "trips";

export type VehicleCostBreakdown = Record<FleetVehicleCostCategory, number>;

export type PdfVehicleSummary = {
  vehicle_id: string;
  vehicle_label: string;
  km: number;
  cost: number;
  costPerKm: number;
  fleetPercent?: number;
  alerts?: string;
  breakdown?: Partial<VehicleCostBreakdown>;
};

export type ExecutivePageData = {
  companyName: string;
  periodLabel: string;
  generatedAtLabel: string;

  vehiclesActive: number;
  vehiclesTotal: number;
  kmTotal: number;
  tripsTotal: number;

  totalCost: number;
  avgCostPerVehicle: number;
  costPerKm: number;

  fuelPercent: number;
  maintenancePercent: number;

  risksCritical: number;
  docsExpired: number;
  vehiclesWithAlerts: number;

  insights: string[];

  vehicleRanking: Array<{
    vehicle_id?: string;
    vehicle_label: string;
    status?: string;
    km: number;
    cost: number;
    costPerKm: number;
    fleetPercent: number;
    alerts?: string;
  }>;

  drivers: Array<{
    name: string;
    trips: number;
    km: number;
    cost: number;
    occ: number;
  }>;

  // Alertas agrupados por severidade
  alerts?: Array<{
    severity: 'critical' | 'high' | 'warning' | 'info' | 'ok';
    message: string;
    vehicle_label?: string;
  }>;

  conclusionText: string;
};

export type FuelItem = {
  occurredAt: string;
  station?: string | null;
  liters?: number | null;
  priceUnit?: number | null;
  totalValue?: number | null;
  odometerKm?: number | null;
  notes?: string | null;
};

export type MaintenanceItem = {
  orderNumber: string;
  planName?: string | null;
  executedAt: string;
  supplier?: string | null;
  type?: string | null;
  totalCost: number;
  items?: Array<{ name: string; qty?: number; unitValue?: number; totalValue?: number }>;
};

export type ExpenseItem = {
  occurredAt: string;
  category?: string | null;
  supplier?: string | null;
  totalCost: number;
  notes?: string | null;
  description?: string | null;
};

export type TireItem = {
  occurredAt: string;
  supplier?: string | null;
  tireModel?: string | null;
  qty?: number | null;
  totalCost: number;
  notes?: string | null;
};

export type DocumentItem = {
  name: string;
  type?: string | null;
  expiryDate?: string | null;
  paidAt?: string | null;
  totalCost?: number | null;
  status?: string | null;
};

export type TripItem = {
  startAt: string;
  endAt?: string | null;
  distanceKm: number;
  driverName?: string | null;
  origin?: string | null;
  destination?: string | null;
};

export type VehicleDetailData = {
  vehicle_id: string;
  vehicle_label: string;
  periodLabel?: string;

  summary: {
    totalCost: number;
    costPerKm: number;
    km: number;
    breakdown: VehicleCostBreakdown;
  };

  fuel: FuelItem[];
  maintenance: MaintenanceItem[];
  tires: TireItem[];
  expenses: ExpenseItem[];
  documents: DocumentItem[];
  trips: TripItem[];
};

export type ConsolidatedPdfInput = {
  executive: ExecutivePageData;
  vehiclesSummary: PdfVehicleSummary[];
  fetchVehicleDetail: (vehicleId: string) => Promise<VehicleDetailData>;
  options: { mode: PdfMode; topN?: number; maxVehicles?: number };
};

/* --------------------------
 * Theme / Helpers
 * -------------------------- */

const A4 = { w: 210, h: 297 };
const M = { l: 14, r: 14, t: 14, b: 14 };
const COLORS = {
  ink: [15, 23, 42] as [number, number, number],
  muted: [100, 116, 139] as [number, number, number],
  border: [226, 232, 240] as [number, number, number],
  panel: [248, 250, 252] as [number, number, number],
  header: [2, 6, 23] as [number, number, number],
  headRow: [241, 245, 249] as [number, number, number],
  zebra: [250, 250, 251] as [number, number, number],
  ok: [16, 185, 129] as [number, number, number],
  warn: [245, 158, 11] as [number, number, number],
  crit: [239, 68, 68] as [number, number, number],
};

function moneyBRL(n: number) {
  return (Number(n) || 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
}
function numberBR(n: number) {
  return (Number(n) || 0).toLocaleString("pt-BR");
}
function clamp(n: number, min: number, max: number) {
  return Math.max(min, Math.min(max, n));
}

function setFont(doc: jsPDF, weight: "normal" | "bold" = "normal") {
  doc.setFont("helvetica", weight);
}

function drawHeader(doc: jsPDF, title: string, subtitle: string) {
  doc.setFillColor(...COLORS.header);
  doc.roundedRect(M.l, M.t, A4.w - M.l - M.r, 22, 4, 4, "F");

  doc.setTextColor(255, 255, 255);
  setFont(doc, "bold");
  doc.setFontSize(12);
  doc.text("FleetONE", M.l + 6, M.t + 8);

  doc.setFontSize(14);
  doc.text(title, M.l + 6, M.t + 16);

  setFont(doc, "normal");
  doc.setFontSize(9);
  doc.setTextColor(226, 232, 240);
  doc.text(subtitle, M.l + 6, M.t + 20);

  doc.setTextColor(...COLORS.ink);
}

function drawFooter(doc: jsPDF, meta: { generatedAtLabel: string }) {
  const page = doc.getNumberOfPages();
  const y = A4.h - 10;

  doc.setDrawColor(...COLORS.border);
  doc.line(M.l, y - 4, A4.w - M.r, y - 4);

  doc.setTextColor(...COLORS.muted);
  doc.setFontSize(8);
  setFont(doc, "normal");
  doc.text(`Gerado em ${meta.generatedAtLabel}`, M.l, y);
  doc.text(`Página ${page}`, A4.w - M.r, y, { align: "right" });

  doc.setTextColor(...COLORS.ink);
}

function panel(doc: jsPDF, x: number, y: number, w: number, h: number) {
  doc.setDrawColor(...COLORS.border);
  doc.setFillColor(255, 255, 255);
  doc.roundedRect(x, y, w, h, 4, 4, "FD");
}

function kpiCard(doc: jsPDF, x: number, y: number, w: number, h: number, opts: {
  label: string;
  value: string;
  sub?: string;
  badge?: { text: string; tone: "ok" | "warn" | "crit" };
}) {
  panel(doc, x, y, w, h);

  doc.setTextColor(...COLORS.muted);
  doc.setFontSize(8);
  setFont(doc, "normal");
  doc.text(opts.label, x + 4, y + 6);

  doc.setTextColor(...COLORS.ink);
  doc.setFontSize(12);
  setFont(doc, "bold");
  doc.text(opts.value, x + 4, y + 14);

  if (opts.sub) {
    doc.setTextColor(...COLORS.muted);
    doc.setFontSize(8);
    setFont(doc, "normal");
    doc.text(opts.sub, x + 4, y + 19);
  }

  if (opts.badge) {
    const col =
      opts.badge.tone === "crit" ? COLORS.crit : opts.badge.tone === "warn" ? COLORS.warn : COLORS.ok;

    doc.setFillColor(...col);
    doc.roundedRect(x + w - 24, y + 4, 20, 6.5, 3, 3, "F");
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(7);
    setFont(doc, "bold");
    doc.text(opts.badge.text, x + w - 14, y + 8.5, { align: "center" });

    doc.setTextColor(...COLORS.ink);
  }
}

function sectionTitle(doc: jsPDF, x: number, y: number, title: string, subtitle?: string) {
  setFont(doc, "bold");
  doc.setFontSize(11);
  doc.setTextColor(...COLORS.ink);
  doc.text(title, x, y);

  if (subtitle) {
    setFont(doc, "normal");
    doc.setFontSize(8.5);
    doc.setTextColor(...COLORS.muted);
    doc.text(subtitle, x, y + 4);
  }
  doc.setTextColor(...COLORS.ink);
}

function tablePro(doc: jsPDF, opts: {
  startY: number;
  head: string[];
  body: any[][];
  marginLeft?: number;
  marginRight?: number;
  marginTopOnNewPage?: number; // margem top para páginas subsequentes (após header + chip)
  onEveryPageHeader?: () => void;
}) {
  // Margem top para páginas com quebra: header (22) + chip (14) + gap (8) = 44
  const topMarginForNewPages = opts.marginTopOnNewPage ?? M.t + 44;
  
  autoTable(doc, {
    startY: opts.startY,
    head: [opts.head],
    body: opts.body,
    theme: "grid",
    styles: {
      fontSize: 8,
      cellPadding: 3,
      textColor: COLORS.ink,
      lineColor: COLORS.border,
    },
    headStyles: {
      fillColor: COLORS.headRow,
      textColor: COLORS.muted,
      fontStyle: "bold",
    },
    alternateRowStyles: { fillColor: COLORS.zebra },
    margin: {
      left: opts.marginLeft ?? M.l,
      right: opts.marginRight ?? M.r,
      top: topMarginForNewPages, // Garante espaço para header + chip em páginas subsequentes
    },
    didDrawPage: () => {
      opts.onEveryPageHeader?.();
    },
  });
  return (doc as any).lastAutoTable?.finalY ?? opts.startY;
}

function severityBadge(costPerKm: number) {
  if (costPerKm >= 2.5) return { text: "Crítico", tone: "crit" as const };
  if (costPerKm >= 1.5) return { text: "Atenção", tone: "warn" as const };
  return { text: "OK", tone: "ok" as const };
}

/* --------------------------
 * Pages
 * -------------------------- */

/**
 * emptyBlockExec - Bloco compacto "Sem dados" para página executiva
 */
function emptyBlockExec(doc: jsPDF, y: number, msg: string): number {
  doc.setFillColor(...COLORS.panel);
  doc.roundedRect(M.l, y, A4.w - M.l - M.r, 12, 2, 2, "F");
  doc.setTextColor(...COLORS.muted);
  doc.setFontSize(9);
  setFont(doc, "normal");
  doc.text(msg, M.l + 4, y + 8);
  doc.setTextColor(...COLORS.ink);
  return y + 16;
}

/**
 * drawRankingTable - Tabela de ranking por custo total
 */
function drawRankingTable(doc: jsPDF, ranking: ExecutivePageData['vehicleRanking'], startY: number): number {
  sectionTitle(doc, M.l, startY, "Ranking por Custo Total", "Top 10 — inclui R$/Km e % da frota");
  const y = startY + 8;

  const rows = (ranking ?? []).slice(0, 10).map((r) => ([
    r.vehicle_label,
    numberBR(r.km),
    moneyBRL(r.cost),
    (Number(r.costPerKm) || 0).toFixed(2),
    `${(Number(r.fleetPercent) || 0).toFixed(1)}%`,
    r.alerts ?? "OK",
  ]));

  if (rows.length === 0) {
    return emptyBlockExec(doc, y, "Sem dados de veículos no período.");
  }

  const finalY = tablePro(doc, {
    startY: y,
    head: ["Veículo", "Km", "Custo", "R$/Km", "% Frota", "Alertas"],
    body: rows,
  });

  return finalY + 8;
}

/**
 * drawInsightsBlock - Bloco de insights com bullets
 */
function drawInsightsBlock(doc: jsPDF, insights: string[], startY: number): number {
  const list = (insights ?? []).slice(0, 6);
  const insightsH = 10 + Math.max(list.length, 3) * 5.5;
  
  panel(doc, M.l, startY, A4.w - M.l - M.r, insightsH);
  sectionTitle(doc, M.l + 4, startY + 8, "Principais Insights do Período");

  setFont(doc, "normal");
  doc.setFontSize(9);

  let ly = startY + 16;
  list.forEach((t) => {
    doc.setTextColor(...COLORS.muted);
    doc.text("•", M.l + 6, ly);
    doc.setTextColor(...COLORS.ink);
    const wrapped = doc.splitTextToSize(String(t), A4.w - M.l - M.r - 16);
    doc.text(wrapped, M.l + 10, ly);
    ly += wrapped.length * 4.5;
  });

  return startY + insightsH + 6;
}

/**
 * drawDriversTable - Tabela de motoristas
 */
function drawDriversTable(doc: jsPDF, drivers: ExecutivePageData['drivers'], startY: number): number {
  sectionTitle(doc, M.l, startY, "Análise por Motorista — Performance Operacional");
  const y = startY + 8;

  const rows = (drivers ?? []).slice(0, 10).map((d) => ([
    d.name,
    String(d.trips),
    numberBR(d.km),
    moneyBRL(d.cost),
    d.km > 0 ? (d.cost / d.km).toFixed(2) : "0,00",
    String(d.occ),
  ]));

  if (rows.length === 0) {
    return emptyBlockExec(doc, y, "Sem dados de motoristas no período.");
  }

  const finalY = tablePro(doc, {
    startY: y,
    head: ["Motorista", "Viagens", "Km", "Custo", "R$/Km", "Ocorrências"],
    body: rows,
  });

  return finalY + 8;
}

/**
 * drawAlertsBlock - Bloco de alertas por severidade
 */
function drawAlertsBlock(doc: jsPDF, alerts: ExecutivePageData['alerts'], risksCritical: number, docsExpired: number, startY: number): number {
  sectionTitle(doc, M.l, startY, "Alertas e Riscos — Classificação por Severidade");
  const y = startY + 8;

  // Se não tiver alertas detalhados, mostrar resumo simples
  if (!alerts || alerts.length === 0) {
    if (risksCritical === 0 && docsExpired === 0) {
      return emptyBlockExec(doc, y, "Sem alertas ativos no período.");
    }
  }

  // Agrupar por severidade
  const grouped = {
    critical: (alerts ?? []).filter(a => a.severity === 'critical'),
    high: (alerts ?? []).filter(a => a.severity === 'high'),
    warning: (alerts ?? []).filter(a => a.severity === 'warning'),
    info: (alerts ?? []).filter(a => a.severity === 'info' || a.severity === 'ok'),
  };

  // Calcular totais (usar dados do KPI se não tiver alerts detalhados)
  const critCount = grouped.critical.length || risksCritical;
  const highCount = grouped.high.length;
  const warnCount = grouped.warning.length;
  const infoCount = grouped.info.length;

  const blockH = 28;
  panel(doc, M.l, y, A4.w - M.l - M.r, blockH);

  // Renderizar badges por severidade em linha
  const badgeW = 42;
  const badgeH = 16;
  const badgeGap = 6;
  const badgeY = y + 6;
  
  const badges = [
    { label: "Crítico", count: critCount, color: COLORS.crit },
    { label: "Alto", count: highCount, color: COLORS.warn },
    { label: "Atenção", count: warnCount, color: [245, 158, 11] as [number, number, number] },
    { label: "Info/OK", count: infoCount, color: COLORS.ok },
  ];

  let bx = M.l + 6;
  badges.forEach((b) => {
    // Fundo do badge
    doc.setFillColor(...b.color);
    doc.roundedRect(bx, badgeY, badgeW, badgeH, 3, 3, "F");

    // Texto do badge
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(10);
    setFont(doc, "bold");
    doc.text(String(b.count), bx + badgeW / 2, badgeY + 7, { align: "center" });
    
    doc.setFontSize(7);
    setFont(doc, "normal");
    doc.text(b.label, bx + badgeW / 2, badgeY + 13, { align: "center" });

    bx += badgeW + badgeGap;
  });

  // Documentos vencidos (à direita)
  if (docsExpired > 0) {
    doc.setTextColor(...COLORS.crit);
    doc.setFontSize(9);
    setFont(doc, "bold");
    doc.text(`${docsExpired} documentos vencidos`, A4.w - M.r - 4, badgeY + 10, { align: "right" });
  }

  doc.setTextColor(...COLORS.ink);
  return y + blockH + 8;
}

/**
 * drawConclusionBlock - Conclusão executiva full width
 */
function drawConclusionBlock(doc: jsPDF, data: ExecutivePageData, startY: number): number {
  // Gerar texto de resumo dinâmico
  const summaryText = `A frota apresenta ${data.vehiclesActive} veículos ativos, com custo total de ${moneyBRL(data.totalCost)} e quilometragem de ${numberBR(data.kmTotal)} km no período analisado. O custo médio por veículo é de ${moneyBRL(data.avgCostPerVehicle)}. ${data.risksCritical > 0 ? `Há ${data.risksCritical} alertas críticos ativos que requerem atenção imediata.` : 'Não há alertas críticos ativos no período.'}`;

  // Extrair recomendações do conclusionText
  const rawConclusion = String(data.conclusionText || "");
  const recommendations = rawConclusion
    .split('\n')
    .filter(Boolean)
    .map(line => line.replace(/^[•\-]\s*/, '').trim())
    .filter(line => line.length > 5)
    .slice(0, 4);

  // Calcular altura do bloco
  const wrappedSummary = doc.splitTextToSize(summaryText, A4.w - M.l - M.r - 12);
  const conclusionH = 20 + wrappedSummary.length * 4 + 8 + recommendations.length * 5 + 8;

  panel(doc, M.l, startY, A4.w - M.l - M.r, conclusionH);
  sectionTitle(doc, M.l + 4, startY + 8, "Conclusão Executiva");

  // Parágrafo resumo
  doc.setFontSize(9);
  setFont(doc, "normal");
  doc.setTextColor(...COLORS.ink);
  doc.text(wrappedSummary, M.l + 4, startY + 16);

  // Subtítulo "Recomendações"
  let recY = startY + 16 + wrappedSummary.length * 4 + 6;
  setFont(doc, "bold");
  doc.setFontSize(9);
  doc.text("Recomendações", M.l + 4, recY);
  recY += 5;

  // Lista de recomendações (bullets)
  setFont(doc, "normal");
  doc.setFontSize(8.5);
  recommendations.forEach((rec) => {
    doc.setTextColor(...COLORS.muted);
    doc.text("•", M.l + 6, recY);
    doc.setTextColor(...COLORS.ink);
    const wrappedRec = doc.splitTextToSize(rec, A4.w - M.l - M.r - 16);
    doc.text(wrappedRec, M.l + 10, recY);
    recY += wrappedRec.length * 4;
  });

  return recY + 4;
}

/**
 * addExecutivePage - Página executiva reorganizada
 * Ordem: KPIs → Ranking → Insights → Motoristas → Alertas → Conclusão
 */
function addExecutivePage(doc: jsPDF, data: ExecutivePageData) {
  const title = "Relatório Consolidado — Visão Executiva";
  const subtitle = `${data.periodLabel} • ${data.companyName}`;

  drawHeader(doc, title, subtitle);

  // KPIs (2 linhas x 3 col)
  const topY = 40;
  const gap = 4;
  const w = (A4.w - M.l - M.r - gap * 2) / 3;
  const h = 22;

  const sev = severityBadge(data.costPerKm);

  kpiCard(doc, M.l, topY, w, h, {
    label: "Veículos ativos",
    value: String(data.vehiclesActive),
    sub: `Total: ${data.vehiclesTotal}`,
  });
  kpiCard(doc, M.l + (w + gap), topY, w, h, {
    label: "Km no período",
    value: numberBR(data.kmTotal),
    sub: `Viagens: ${data.tripsTotal}`,
  });
  kpiCard(doc, M.l + 2 * (w + gap), topY, w, h, {
    label: "Custo total",
    value: moneyBRL(data.totalCost),
    sub: `Média/veículo: ${moneyBRL(data.avgCostPerVehicle)}`,
  });

  kpiCard(doc, M.l, topY + h + gap, w, h, {
    label: "Custo por km",
    value: moneyBRL(data.costPerKm),
    sub: "Meta sugerida: < 2,50",
    badge: sev,
  });
  kpiCard(doc, M.l + (w + gap), topY + h + gap, w, h, {
    label: "% combustível",
    value: `${data.fuelPercent.toFixed(1)}%`,
    sub: `Manutenção: ${data.maintenancePercent.toFixed(1)}%`,
    badge: data.fuelPercent >= 65 ? { text: "Atenção", tone: "warn" } : { text: "OK", tone: "ok" },
  });
  kpiCard(doc, M.l + 2 * (w + gap), topY + h + gap, w, h, {
    label: "Riscos/Compliance",
    value: `${data.risksCritical} críticos`,
    sub: `${data.docsExpired} docs vencidos • ${data.vehiclesWithAlerts} veículos`,
    badge: data.risksCritical > 0 ? { text: "Ação", tone: "crit" } : { text: "OK", tone: "ok" },
  });

  // Posição após KPIs
  let y = topY + (h + gap) * 2 + 8;

  // 1) RANKING POR CUSTO TOTAL (logo após KPIs)
  y = drawRankingTable(doc, data.vehicleRanking, y);

  // 2) PRINCIPAIS INSIGHTS DO PERÍODO
  y = drawInsightsBlock(doc, data.insights, y);

  // Verificar se precisa de nova página para próximos blocos
  const bottomLimit = A4.h - M.b - 60;
  if (y > bottomLimit) {
    doc.addPage();
    drawHeader(doc, title, subtitle);
    y = M.t + 28;
  }

  // 3) ANÁLISE POR MOTORISTA (tabela)
  y = drawDriversTable(doc, data.drivers, y);

  // Verificar espaço para alertas
  if (y > bottomLimit) {
    doc.addPage();
    drawHeader(doc, title, subtitle);
    y = M.t + 28;
  }

  // 4) ALERTAS E RISCOS — CLASSIFICAÇÃO POR SEVERIDADE
  y = drawAlertsBlock(doc, data.alerts, data.risksCritical, data.docsExpired, y);

  // Verificar espaço para conclusão
  if (y > bottomLimit) {
    doc.addPage();
    drawHeader(doc, title, subtitle);
    y = M.t + 28;
  }

  // 5) CONCLUSÃO EXECUTIVA (full width)
  y = drawConclusionBlock(doc, data, y);

  // Footer será desenhado no loop final
}

/* --------------------------
 * Layout Helpers (Stacked Layout)
 * -------------------------- */

/**
 * ensureSpace - Verifica se há espaço na página atual.
 * Se não houver, quebra página e chama onNewPage.
 */
function ensureSpace(
  doc: jsPDF,
  currentY: number,
  neededHeight: number,
  onNewPage: () => void
): number {
  const bottomLimit = A4.h - M.b - 12; // reserva rodapé
  if (currentY + neededHeight > bottomLimit) {
    doc.addPage();
    onNewPage();
    return M.t + 50; // após header (22) + chip (14) + gap (14)
  }
  return currentY;
}

/**
 * emptyBlock - Bloco compacto "Sem registros no período"
 */
function emptyBlock(doc: jsPDF, y: number, msg: string): number {
  doc.setFillColor(...COLORS.panel);
  doc.roundedRect(M.l, y, A4.w - M.l - M.r, 12, 2, 2, "F");
  doc.setTextColor(...COLORS.muted);
  doc.setFontSize(9);
  setFont(doc, "normal");
  doc.text(msg, M.l + 4, y + 8);
  doc.setTextColor(...COLORS.ink);
  return y + 16;
}

/**
 * drawCategory - Renderiza título + (tabela ou empty block)
 * Só renderiza tabela se houver dados.
 */
function drawCategory(
  doc: jsPDF,
  opts: {
    title: string;
    columns: string[];
    rows: any[][];
    emptyMsg: string;
  },
  y: number,
  onNewPage: () => void
): number {
  // Título da categoria (sempre)
  y = ensureSpace(doc, y, 18, onNewPage);
  sectionTitle(doc, M.l, y, opts.title);
  y += 6;

  // Sem dados → bloco compacto
  if (!opts.rows || opts.rows.length === 0) {
    return emptyBlock(doc, y, opts.emptyMsg);
  }

  // Tabela com dados
  y = ensureSpace(doc, y, 24, onNewPage);
  const finalY = tablePro(doc, {
    startY: y,
    head: opts.columns,
    body: opts.rows,
    marginTopOnNewPage: M.t + 50, // Após header (22) + chip (14) + gap (14)
    onEveryPageHeader: onNewPage,
  });

  return finalY + 6;
}

/**
 * drawVehicleHeaderChip - Desenha o chip do veículo no topo
 */
function drawVehicleHeaderChip(doc: jsPDF, vehicleLabel: string) {
  const y0 = M.t + 26;
  panel(doc, M.l, y0, A4.w - M.l - M.r, 14);
  setFont(doc, "normal");
  doc.setFontSize(9);
  doc.setTextColor(...COLORS.muted);
  doc.text("Veículo:", M.l + 4, y0 + 9);
  setFont(doc, "bold");
  doc.setTextColor(...COLORS.ink);
  doc.text(vehicleLabel, M.l + 22, y0 + 9);
}

/**
 * drawStackedVehicleKpis - 3 KPIs em linha
 */
function drawStackedVehicleKpis(
  doc: jsPDF,
  sum: { totalCost: number; costPerKm: number; km: number },
  y: number
): number {
  const gap = 4;
  const w = (A4.w - M.l - M.r - gap * 2) / 3;
  const h = 20;

  kpiCard(doc, M.l, y, w, h, {
    label: "Custo total do veículo",
    value: moneyBRL(sum.totalCost),
  });
  kpiCard(doc, M.l + (w + gap), y, w, h, {
    label: "Custo por km",
    value: moneyBRL(sum.costPerKm),
    badge: severityBadge(sum.costPerKm),
  });
  kpiCard(doc, M.l + 2 * (w + gap), y, w, h, {
    label: "Km no período",
    value: numberBR(sum.km),
  });

  return y + h + 6;
}

/**
 * drawStackedCostComposition - Composição horizontal compacta
 */
function drawStackedCostComposition(
  doc: jsPDF,
  breakdown: Partial<VehicleCostBreakdown> | undefined,
  totalCost: number,
  y: number
): number {
  sectionTitle(doc, M.l, y, "Composição do custo");
  y += 8;

  const b = breakdown ?? { fuel: 0, maintenance: 0, tires: 0, expenses: 0, documents: 0, trips: 0 };
  const total = totalCost || 1;

  const items: Array<{ k: FleetVehicleCostCategory; label: string }> = [
    { k: "fuel", label: "Combustível" },
    { k: "maintenance", label: "Manutenção" },
    { k: "tires", label: "Pneus" },
    { k: "expenses", label: "Despesas" },
    { k: "documents", label: "Documentos" },
    { k: "trips", label: "Viagens" },
  ];

  const barX = M.l + 70;
  const barW = A4.w - M.l - M.r - 72;
  const rowH = 7;

  items.forEach((it) => {
    const val = b[it.k] ?? 0;
    const pct = Math.round((val / total) * 100);

    doc.setFontSize(8.5);
    setFont(doc, "normal");
    doc.setTextColor(...COLORS.ink);
    doc.text(it.label, M.l, y);

    doc.setTextColor(...COLORS.muted);
    doc.text(`${moneyBRL(val)}  ${pct}%`, M.l + 28, y);

    // Barra fundo
    doc.setFillColor(...COLORS.panel);
    doc.roundedRect(barX, y - 3.5, barW, 4, 2, 2, "F");

    // Barra preenchida
    doc.setFillColor(...COLORS.warn);
    const pw = Math.max(1, (barW * pct) / 100);
    doc.roundedRect(barX, y - 3.5, pw, 4, 2, 2, "F");

    y += rowH;
  });

  doc.setTextColor(...COLORS.ink);
  return y + 4;
}

/* --------------------------
 * addVehiclePage - LAYOUT EMPILHADO (refatorado)
 * -------------------------- */

function addVehiclePage(doc: jsPDF, meta: ExecutivePageData, vehicle: PdfVehicleSummary, detail: VehicleDetailData) {
  // Sempre começa veículo em página nova
  doc.addPage();

  const title = "Detalhamento por veículo — Custos e Itens";
  const subtitle = `${meta.periodLabel} • ${meta.companyName}`;

  // Função para redesenhar header quando quebra página
  const onNewPage = () => {
    drawHeader(doc, title, subtitle);
    drawVehicleHeaderChip(doc, detail.vehicle_label || vehicle.vehicle_label);
  };

  // Header inicial
  onNewPage();

  // Y base após header (22) + chip (14) + gap
  let y = M.t + 44;

  // 1) Resumo (summary) com fallback do vehicle
  const sum = detail.summary ?? {
    totalCost: vehicle.cost ?? 0,
    costPerKm: vehicle.costPerKm ?? 0,
    km: vehicle.km ?? 0,
    breakdown: (vehicle.breakdown as VehicleCostBreakdown) ?? { fuel: 0, maintenance: 0, tires: 0, expenses: 0, documents: 0, trips: 0 },
  };

  // 2) KPIs (3 cards)
  y = ensureSpace(doc, y, 28, onNewPage);
  y = drawStackedVehicleKpis(doc, sum, y);

  // 3) Composição de custo (compacta)
  y = ensureSpace(doc, y, 54, onNewPage);
  y = drawStackedCostComposition(doc, sum.breakdown, sum.totalCost, y);

  // 4) TABELAS EMPILHADAS — só renderiza se tiver dados

  // Manutenção
  y = drawCategory(doc, {
    title: "Manutenção / OS",
    columns: ["OS", "Plano", "Data", "Fornecedor", "Valor"],
    rows: (detail.maintenance ?? []).slice(0, 20).map((m) => [
      m.orderNumber || "-",
      m.planName || "-",
      m.executedAt || "-",
      m.supplier || "-",
      moneyBRL(m.totalCost || 0),
    ]),
    emptyMsg: "Sem ordens de serviço no período.",
  }, y, onNewPage);

  // Combustível
  y = drawCategory(doc, {
    title: "Combustível",
    columns: ["Data", "Posto", "Litros", "R$/L", "Valor"],
    rows: (detail.fuel ?? []).slice(0, 25).map((f) => {
      const v = (f.totalValue ?? ((f.liters || 0) * (f.priceUnit || 0))) || 0;
      return [
        f.occurredAt || "-",
        f.station || "-",
        f.liters != null ? String(Number(f.liters).toFixed(1)) : "-",
        f.priceUnit != null ? String(Number(f.priceUnit).toFixed(2)) : "-",
        moneyBRL(v),
      ];
    }),
    emptyMsg: "Sem abastecimentos no período.",
  }, y, onNewPage);

  // Pneus
  y = drawCategory(doc, {
    title: "Pneus",
    columns: ["Data", "Descrição", "Valor"],
    rows: (detail.tires ?? []).slice(0, 15).map((t) => [
      t.occurredAt || "-",
      t.tireModel || t.notes || "-",
      moneyBRL(t.totalCost || 0),
    ]),
    emptyMsg: "Sem lançamentos de pneus no período.",
  }, y, onNewPage);

  // Despesas
  y = drawCategory(doc, {
    title: "Despesas",
    columns: ["Data", "Categoria", "Valor"],
    rows: (detail.expenses ?? []).slice(0, 15).map((e) => [
      e.occurredAt || "-",
      e.category || e.description || "-",
      moneyBRL(e.totalCost || 0),
    ]),
    emptyMsg: "Sem despesas no período.",
  }, y, onNewPage);

  // Documentos
  y = drawCategory(doc, {
    title: "Documentos",
    columns: ["Documento", "Tipo", "Venc.", "Status"],
    rows: (detail.documents ?? []).slice(0, 15).map((d) => [
      d.name || "-",
      d.type || "-",
      d.expiryDate || "-",
      d.status || "-",
    ]),
    emptyMsg: "Sem documentos no período.",
  }, y, onNewPage);

  // Viagens
  y = drawCategory(doc, {
    title: "Viagens",
    columns: ["Início", "Fim", "Km", "Motorista", "Rota"],
    rows: (detail.trips ?? []).slice(0, 15).map((t) => [
      t.startAt || "-",
      t.endAt || "-",
      numberBR(t.distanceKm || 0),
      t.driverName || "-",
      [t.origin, t.destination].filter(Boolean).join(" → ") || "-",
    ]),
    emptyMsg: "Sem viagens no período.",
  }, y, onNewPage);

  // Footer será desenhado no loop final de generateConsolidatedPDF_Pro
}

/* --------------------------
 * Public API
 * -------------------------- */

export async function generateConsolidatedPDF_Pro(input: ConsolidatedPdfInput): Promise<Blob> {
  const doc = new jsPDF({ unit: "mm", format: "a4", compress: true });

  // Página 1 (executivo)
  addExecutivePage(doc, input.executive);

  const mode = input.options?.mode ?? "top-n";
  if (mode === "executive") {
    return doc.output("blob");
  }

  // Seleção de veículos
  const topN = input.options?.topN ?? 10;
  const maxVehicles = input.options?.maxVehicles ?? 80;

  let vehiclesToDetail: PdfVehicleSummary[] =
    mode === "top-n"
      ? (input.vehiclesSummary ?? []).slice(0, topN)
      : (input.vehiclesSummary ?? []);

  const truncated = vehiclesToDetail.length > maxVehicles;
  if (truncated) vehiclesToDetail = vehiclesToDetail.slice(0, maxVehicles);

  // Render por veículo
  for (const v of vehiclesToDetail) {
    const detail = await input.fetchVehicleDetail(v.vehicle_id);
    addVehiclePage(doc, input.executive, v, detail);
  }

  // Aviso de truncamento (opcional)
  if (truncated) {
    doc.addPage();
    drawHeader(
      doc,
      "Aviso de truncamento",
      `${input.executive.periodLabel} • ${input.executive.companyName}`
    );
    panel(doc, M.l, 45, A4.w - M.l - M.r, 40);
    sectionTitle(doc, M.l + 4, 55, "Relatório completo limitado", "Proteção contra PDFs gigantes");
    setFont(doc, "normal");
    doc.setFontSize(10);
    doc.setTextColor(...COLORS.ink);
    doc.text(
      doc.splitTextToSize(
        `Este PDF foi limitado a ${maxVehicles} veículos detalhados para garantir performance e legibilidade. ` +
          `Use o modo Top N para focar nos veículos de maior custo ou aumente o limite de veículos no filtro.`,
        A4.w - M.l - M.r - 8
      ),
      M.l + 4,
      65
    );
  }

  // Loop final: desenhar footer em TODAS as páginas com "Página X de Y"
  const totalPages = doc.getNumberOfPages();
  for (let p = 1; p <= totalPages; p++) {
    doc.setPage(p);
    const footerY = A4.h - 10;
    
    // Linha separadora
    doc.setDrawColor(...COLORS.border);
    doc.line(M.l, footerY - 4, A4.w - M.r, footerY - 4);
    
    // Texto do footer
    doc.setTextColor(...COLORS.muted);
    doc.setFontSize(8);
    setFont(doc, "normal");
    doc.text(`Gerado em ${input.executive.generatedAtLabel}`, M.l, footerY);
    doc.text(`Página ${p} de ${totalPages}`, A4.w - M.r, footerY, { align: "right" });
  }

  return doc.output("blob");
}

/* --------------------------
 * BRIDGE (build input)
 * -------------------------- */

function nowLabelBR() {
  const d = new Date();
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const yyyy = d.getFullYear();
  const hh = String(d.getHours()).padStart(2, "0");
  const mi = String(d.getMinutes()).padStart(2, "0");
  return `${dd}/${mm}/${yyyy} ${hh}:${mi}`;
}

function safeNum(n: any, fallback = 0) {
  const x = Number(n);
  return Number.isFinite(x) ? x : fallback;
}

export function buildConsolidatedPdfInput(params: {
  reportData: any;
  vehiclesSummary: PdfVehicleSummary[];
  periodLabel: string;
  companyName: string;

  allVehiclesDetails?: Map<string, VehicleDetailData>;
  fetchVehicleDetail?: (vehicleId: string) => Promise<VehicleDetailData>;

  options: { mode: PdfMode; topN?: number; maxVehicles?: number };
}): ConsolidatedPdfInput {
  const { reportData, vehiclesSummary, periodLabel, companyName } = params;

  const kpis = reportData?.summaryKpis ?? reportData?.kpis ?? reportData?.summary ?? reportData ?? {};

  const insights: string[] =
    reportData?.insights ??
    reportData?.page1?.insights ??
    [
      "Top veículos concentram a maior parte do custo — priorize ação nesses ativos.",
      "Combustível e manutenção são os maiores vetores de economia.",
      "Regularize documentos e trate alertas críticos para reduzir risco operacional.",
    ];

  const drivers = reportData?.byDriver?.rows ?? reportData?.drivers ?? [];

  const vehicleRanking =
    (reportData?.byVehicle?.rows ?? vehiclesSummary ?? [])
      .slice(0, 10)
      .map((v: any) => ({
        vehicle_id: String(v.vehicle_id ?? v.id ?? ""),
        vehicle_label: String(v.vehicle_label ?? v.label ?? "-"),
        km: safeNum(v.km ?? v.kpis?.totalKm ?? v.distance_km ?? 0),
        cost: safeNum(v.cost ?? v.kpis?.totalCost ?? v.total_cost ?? 0),
        costPerKm: safeNum(v.costPerKm ?? v.kpis?.costPerKm ?? v.cost_per_km ?? 0),
        fleetPercent: safeNum(v.fleetPercent ?? v.kpis?.fleetPercent ?? v.fleet_percent ?? 0),
        alerts: (Array.isArray(v.alerts) && v.alerts.length > 0)
          ? `${v.alerts.length} alerta${v.alerts.length > 1 ? 's' : ''}`
          : String(v.alertBadge ?? "OK"),
      }));

  // Calcular totais para executive
  const totalCost = vehiclesSummary.reduce((acc, v) => acc + (v.cost || 0), 0);
  const totalKm = vehiclesSummary.reduce((acc, v) => acc + (v.km || 0), 0);
  const vehiclesCount = vehiclesSummary.length;

  const executive: ExecutivePageData = {
    companyName,
    periodLabel,
    generatedAtLabel: nowLabelBR(),

    vehiclesActive: safeNum(kpis.vehiclesActive ?? kpis.vehicles_active ?? vehiclesCount, 0),
    vehiclesTotal: safeNum(kpis.vehiclesTotal ?? kpis.vehicles_total ?? vehiclesCount, 0),
    kmTotal: safeNum(kpis.kmTotal ?? kpis.km_total ?? kpis.totalKm ?? totalKm, 0),
    tripsTotal: safeNum(kpis.tripsTotal ?? kpis.trips_total ?? kpis.totalTrips ?? 0, 0),

    totalCost: safeNum(kpis.totalCost ?? kpis.total_cost ?? totalCost, 0),
    avgCostPerVehicle: vehiclesCount > 0 ? totalCost / vehiclesCount : 0,
    costPerKm: totalKm > 0 ? totalCost / totalKm : 0,

    fuelPercent: safeNum(kpis.fuelPercent ?? kpis.fuel_percent ?? 0, 0),
    maintenancePercent: safeNum(kpis.maintenancePercent ?? kpis.maintenance_percent ?? 0, 0),

    risksCritical: safeNum(kpis.risksCritical ?? kpis.risks_critical ?? kpis.criticalAlerts ?? 0, 0),
    docsExpired: safeNum(kpis.docsExpired ?? kpis.docs_expired ?? kpis.expiredDocs ?? 0, 0),
    vehiclesWithAlerts: safeNum(kpis.vehiclesWithAlerts ?? kpis.vehicles_with_alerts ?? 0, 0),

    insights,
    vehicleRanking,

    drivers: (drivers ?? []).slice(0, 12).map((d: any) => ({
      name: String(d.driver_label ?? d.driver_name ?? d.name ?? "-"),
      trips: safeNum(d.kpis?.tripCount ?? d.trips ?? d.trips_total ?? 0),
      km: safeNum(d.kpis?.totalKm ?? d.km ?? d.distance_km ?? 0),
      cost: safeNum(d.kpis?.totalCost ?? d.cost ?? d.total_cost ?? 0),
      occ: safeNum(d.kpis?.occurrences ?? d.kpis?.alertCount ?? d.occ ?? 0),
    })),

    // Mapear alertas por severidade
    alerts: (reportData?.alerts ?? []).slice(0, 20).map((a: any) => ({
      severity: (a.severity ?? a.priority ?? 'info') as 'critical' | 'high' | 'warning' | 'info' | 'ok',
      message: String(a.message ?? a.title ?? a.description ?? '-'),
      vehicle_label: a.vehicle_label ?? a.vehicleLabel ?? null,
    })),

    conclusionText:
      reportData?.conclusion?.text ??
      reportData?.page1?.conclusion?.text ??
      "Recomendações:\n• Priorizar os veículos de maior custo\n• Aumentar controle de abastecimento\n• Tratar alertas críticos e documentos vencidos",
  };

  const fetchVehicleDetail =
    params.fetchVehicleDetail ??
    (async (vehicleId: string) => {
      const cached = params.allVehiclesDetails?.get(vehicleId);
      if (cached) return cached;

      // fallback super seguro
      const s = vehiclesSummary.find((x) => x.vehicle_id === vehicleId);
      return {
        vehicle_id: vehicleId,
        vehicle_label: s?.vehicle_label ?? `Veículo ${vehicleId}`,
        periodLabel,
        summary: {
          totalCost: s?.cost ?? 0,
          costPerKm: s?.costPerKm ?? 0,
          km: s?.km ?? 0,
          breakdown: (s?.breakdown as VehicleCostBreakdown) ?? {
            fuel: 0, maintenance: 0, tires: 0, expenses: 0, documents: 0, trips: 0,
          },
        },
        fuel: [],
        maintenance: [],
        tires: [],
        expenses: [],
        documents: [],
        trips: [],
      };
    });

  return {
    executive,
    vehiclesSummary,
    fetchVehicleDetail,
    options: params.options,
  };
}
